import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react"
import { withFormik, Form, Field } from "formik"
import * as Yup from "yup"
import PropTypes from "prop-types"
import Grid from "@material-ui/core/Grid"
import styles from "./SignUpApplyForm.module.scss"
import Input from "../../components/Input/Input"
import TextField from "@material-ui/core/TextField"
import FormControl from "@material-ui/core/FormControl"
import FormLabel from "@material-ui/core/FormLabel"
import RadioGroup from "@material-ui/core/RadioGroup"
import Radio from "@material-ui/core/Radio"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import Checkbox from "@material-ui/core/Checkbox"
import Dropdown from "../../components/Dropdown/Dropdown"
import Button from "@material-ui/core/Button"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import {
  revenueRangeItems,
  financialServicesTypeItems,
  countryItems,
  professionalLevelItems,
  regionItems,
} from "./SignUpApplyValuesHelper"
import {
  getRequiredFields,
  isFieldVisible,
  initFieldsConfig,
} from "./SignUpApplyFieldsConfigApi"
import ConditionalField from "./ConditionalField"

const howDidYouLearnOptions = [
  { id: "cold_call", value: "Cold Call" },
  { id: "customer_referral", value: "Customer Referral" },
  { id: "employee_referral", value: "Employee Referral" },
  { id: "event", value: "Event" },
  { id: "facebook", value: "Facebook" },
  { id: "google_plus", value: "Google+" },
  { id: "linkedin", value: "LinkedIn" },
  { id: "partner", value: "Partner" },
  { id: "twitter_x", value: "Twitter (X)" },
  { id: "web_search", value: "Web Search" },
]

const SignUpApplyStep2Form = ({
  values,
  errors,
  handleSubmit,
  handleChange,
  handleBlur,
  touched,
  isSubmitting,
  onSubmit,
  onValidityChange,
  dirty,
  setFieldTouched,
  initialValues,
  setFieldValue,
  step1Data,
  validateForm,
  setTouched,
}) => {
  const [goodsServicesFiles, setGoodsServicesFiles] = useState([])
  const [certificationsFiles, setCertificationsFiles] = useState([])
  const [programsServicesFiles, setProgramsServicesFiles] = useState([])
  const [qualityCertificationsFiles, setQualityCertificationsFiles] = useState(
    [],
  )
  const [qualityCertificationsUploadError, setQualityCertificationsUploadError] =
    useState("")
  const [configInitialized, setConfigInitialized] = useState(false)

  // Initialize the field configuration when the component mounts or when business type or category changes
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (
          step1Data &&
          step1Data.whichBusinessType &&
          step1Data.bestDescribes
        ) {
          await initFieldsConfig(
            step1Data.whichBusinessType,
            step1Data.bestDescribes,
          )
          setConfigInitialized(true)
        }
      } catch (error) {
        console.error("Error initializing field config:", error)
      }
    }

    fetchConfig()
  }, [step1Data])

  // Mark fields as touched when initialValues are provided
  useEffect(() => {
    if (initialValues) {
      const fields = [
        "companyName",
        "companyPhonenumber",
        "streetAddress",
        "stateRegion",
        "zipCode",
        "country",
        "website",
        "services",
        "terms",
        "terms1",
        "customers",
        "revenueRange",
        "revenueRangeAUM",
        "goodsServicesUploadOption",
        "goodsServicesUpload",
        "certificationsUploadOption",
        "certificationsUpload",
        "certifications",
        "qualityCertifications",
        "qualityCertificationsUploadOption",
        "qualityCertificationsUpload",
        "businessRegions",
        "additionalInfo",
        "howDidYouLearn",
        "organizationName",
        "organizationPhone",
        "title",
        "areaOfSpecialization",
        "financialServicesType",
        "fundingOpportunities",
        "industrySpecialization",
        "targetCompanyQualifications",
        "programsServicesUpload",
        "providesServicesInMoreThanThreeStates",
        "isSolopreneur",
        "linkedinProfile",
        "professionalLevel",
        "region",
      ]
      fields.forEach((field) => {
        if (initialValues[field]) {
          setFieldTouched(field, true, false)
        }
      })
    }
  }, [initialValues, setFieldTouched])

  // Handle N/A preference selection
  useEffect(() => {
    if (values.preferenceNA) {
      // If N/A is selected, uncheck all other preferences
      setFieldValue("preference1", false)
      setFieldValue("preference2", false)
      setFieldValue("preference3", false)
      setFieldValue("preference4", false)
      setFieldValue("preference5", false)
      setFieldValue("preference6", false)
      setFieldValue("preference7", false)
      setFieldValue("preference8", false)
      setFieldValue("preference9", false)
      setFieldValue("preference10", false)
      setFieldValue("preference11", false)
      setFieldValue("preference12", false)
      setFieldValue("preference13", false)
      setFieldValue("preference14", false)
      setFieldValue("preference15", false)
      setFieldValue("preference16", false)
      setFieldValue("preference17", false)
      setFieldValue("preference18", false)
    }
  }, [values.preferenceNA, setFieldValue])

  // If user selects "No" for diverse-owned/led, default designations to N/A
  useEffect(() => {
    if (values.terms1 === "no") {
      setFieldValue("preferenceNA", true)
      setFieldTouched("diversityDesignations", true, false)
    } else if (values.terms1 === "yes" && values.preferenceNA) {
      setFieldValue("preferenceNA", false)
    }
  }, [values.terms1, values.preferenceNA, setFieldValue, setFieldTouched])

  // For backward compatibility with existing code
  let isSeeker = values.whichBusinessType === "company_seeker"
  const isProvider = values.whichBusinessType === "company_provider"

  // Get the business type and category for use with ConditionalField
  const businessType = values.whichBusinessType
  const category = values.bestDescribes

  // Handle other preference selections
  useEffect(() => {
    // If any other preference is selected, uncheck N/A
    if (
      values.preference1 ||
      values.preference2 ||
      values.preference3 ||
      values.preference4 ||
      values.preference5 ||
      values.preference6 ||
      values.preference7 ||
      values.preference8 ||
      values.preference9 ||
      values.preference10 ||
      values.preference11 ||
      values.preference12 ||
      values.preference13 ||
      values.preference14 ||
      values.preference15 ||
      values.preference16 ||
      values.preference17 ||
      values.preference18
    ) {
      setFieldValue("preferenceNA", false)
    }
  }, [
    values.preference1,
    values.preference2,
    values.preference3,
    values.preference4,
    values.preference5,
    values.preference6,
    values.preference7,
    values.preference8,
    values.preference9,
    values.preference10,
    values.preference11,
    values.preference12,
    values.preference13,
    values.preference14,
    values.preference15,
    values.preference16,
    values.preference17,
    values.preference18,
    setFieldValue,
  ])

  // Handle businessRegions field based on region checkboxes
  useEffect(() => {
    // Set businessRegions to true if at least one region is selected
    const hasSelectedRegion =
      values.regionUS ||
      values.regionCanada ||
      values.regionEurope ||
      values.regionAsiaPacific ||
      values.regionLatinAmerica ||
      values.regionMiddleEast ||
      values.regionAfrica

    setFieldValue("businessRegions", hasSelectedRegion)
    // Also mark the field as touched when any region is selected
    if (hasSelectedRegion) {
      setFieldTouched("businessRegions", true, false)
    }
  }, [
    values.regionUS,
    values.regionCanada,
    values.regionEurope,
    values.regionAsiaPacific,
    values.regionLatinAmerica,
    values.regionMiddleEast,
    values.regionAfrica,
    setFieldValue,
    setFieldTouched,
  ])

  // Handle diversityDesignations field based on preference checkboxes
  useEffect(() => {
    // Set diversityDesignations to true if at least one preference is selected
    const hasSelectedPreference =
      values.preference1 ||
      values.preference2 ||
      values.preference3 ||
      values.preference4 ||
      values.preference5 ||
      values.preference6 ||
      values.preference7 ||
      values.preference8 ||
      values.preference9 ||
      values.preference10 ||
      values.preference11 ||
      values.preference12 ||
      values.preference13 ||
      values.preference14 ||
      values.preference15 ||
      values.preference16 ||
      values.preference17 ||
      values.preference18 ||
      values.preferenceNA

    setFieldValue("diversityDesignations", hasSelectedPreference)
    // Also mark the field as touched when any preference is selected
    if (hasSelectedPreference) {
      setFieldTouched("diversityDesignations", true, false)
    }
  }, [
    values.preference1,
    values.preference2,
    values.preference3,
    values.preference4,
    values.preference5,
    values.preference6,
    values.preference7,
    values.preference8,
    values.preference9,
    values.preference10,
    values.preference11,
    values.preference12,
    values.preference13,
    values.preference14,
    values.preference15,
    values.preference16,
    values.preference17,
    values.preference18,
    values.preferenceNA,
    setFieldValue,
    setFieldTouched,
  ])

  // Handle howDidYouLearn field to mark it as touched when it has a value
  useEffect(() => {
    if (values.howDidYouLearn) {
      setFieldTouched("howDidYouLearn", true, false)
    }
  }, [values.howDidYouLearn, setFieldTouched])

  // Sync goodsServicesUpload field value with files array
  useEffect(() => {
    if (values.goodsServicesUploadOption === "upload") {
      const hasFiles = goodsServicesFiles.length > 0
      // Always update the field value to ensure it's in sync
      setFieldValue("goodsServicesUpload", hasFiles, true)
      if (hasFiles) {
        setFieldTouched("goodsServicesUpload", true, false)
      }
    } else if (values.goodsServicesUploadOption === "text") {
      // Reset to false when switching to text option
      setFieldValue("goodsServicesUpload", false, false)
    }
  }, [goodsServicesFiles, values.goodsServicesUploadOption, setFieldValue, setFieldTouched])

  // Sync certificationsUpload field value with files array
  useEffect(() => {
    if (values.certificationsUploadOption === "upload") {
      const hasFiles = certificationsFiles.length > 0
      // Always update the field value to ensure it's in sync
      setFieldValue("certificationsUpload", hasFiles, true)
      if (hasFiles) {
        setFieldTouched("certificationsUpload", true, false)
      }
    } else if (values.certificationsUploadOption === "text") {
      // Reset to false when switching to text option
      setFieldValue("certificationsUpload", false, false)
    }
  }, [certificationsFiles, values.certificationsUploadOption, setFieldValue, setFieldTouched])

  // Sync qualityCertificationsUpload field value with files array
  useEffect(() => {
    if (values.qualityCertificationsUploadOption === "upload") {
      const hasFiles = qualityCertificationsFiles.length > 0
      setFieldValue("qualityCertificationsUpload", hasFiles, true)
      if (hasFiles) {
        setFieldTouched("qualityCertificationsUpload", true, false)
      }
    } else if (values.qualityCertificationsUploadOption === "text") {
      // Only clear local state if there's something to clear, otherwise this
      // effect can loop by setting new empty-array/string values every render.
      if (qualityCertificationsFiles.length > 0) {
        setQualityCertificationsFiles([])
      }
      if (qualityCertificationsUploadError) {
        setQualityCertificationsUploadError("")
      }
      setFieldValue("qualityCertificationsUpload", false, false)
    }
  }, [
    qualityCertificationsFiles,
    qualityCertificationsUploadError,
    values.qualityCertificationsUploadOption,
    setFieldValue,
    setFieldTouched,
  ])

  useEffect(() => {
    // Get required fields based on business type and category from configuration
    const businessType = values.whichBusinessType
    const category = values.bestDescribes
    const requiredFields = getRequiredFields(businessType, category, values)

    const hasErrors = requiredFields.some(
      (field) => errors[field] && touched[field],
    )
    const allFieldsTouched = requiredFields.every((field) => touched[field])
    const allFieldsFilled = requiredFields.every((field) => values[field])

    // Debug validation issues
    console.log("[DEBUG] Form Validation Status:", {
      businessType: values.whichBusinessType,
      bestDescribes: values.bestDescribes,
      requiredFields,
      fieldsWithErrors: requiredFields.filter(
        (field) => errors[field] && touched[field],
      ),
      untouchedFields: requiredFields.filter((field) => !touched[field]),
      emptyFields: requiredFields.filter((field) => !values[field]),
      isValid: !hasErrors && allFieldsTouched && allFieldsFilled && dirty,
    })

    onValidityChange(!hasErrors && allFieldsTouched && allFieldsFilled && dirty)
  }, [values, errors, touched, dirty, onValidityChange])

  const handleGoodsServicesFileChange = (event) => {
    const newFiles = Array.from(event.target.files || [])
    setGoodsServicesFiles((prevFiles) => {
      // Combine existing files with new files, avoiding duplicates
      const combinedFiles = [...prevFiles]
      newFiles.forEach((newFile) => {
        // Check if file already exists (by name and size)
        const isDuplicate = prevFiles.some(
          (prevFile) =>
            prevFile.name === newFile.name && prevFile.size === newFile.size,
        )
        if (!isDuplicate) {
          combinedFiles.push(newFile)
        }
      })
      // Immediately update field value and mark as touched when files are present
      const hasFiles = combinedFiles.length > 0
      if (hasFiles) {
        setFieldValue("goodsServicesUpload", true, true)
        setFieldTouched("goodsServicesUpload", true, false)
      }
      return combinedFiles
    })
    // Reset the input value to allow selecting the same file again if needed
    event.target.value = ""
  }

  const handleCertificationsFileChange = (event) => {
    const newFiles = Array.from(event.target.files || [])
    setCertificationsFiles((prevFiles) => {
      // Combine existing files with new files, avoiding duplicates
      const combinedFiles = [...prevFiles]
      newFiles.forEach((newFile) => {
        // Check if file already exists (by name and size)
        const isDuplicate = prevFiles.some(
          (prevFile) =>
            prevFile.name === newFile.name && prevFile.size === newFile.size,
        )
        if (!isDuplicate) {
          combinedFiles.push(newFile)
        }
      })
      // Immediately update field value and mark as touched when files are present
      const hasFiles = combinedFiles.length > 0
      if (hasFiles) {
        setFieldValue("certificationsUpload", true, true)
        setFieldTouched("certificationsUpload", true, false)
      }
      return combinedFiles
    })
    // Reset the input value to allow selecting the same file again if needed
    event.target.value = ""
  }

  const MAX_QUALITY_CERTIFICATIONS_TOTAL_BYTES = 10 * 1024 * 1024
  const QUALITY_CERT_ALLOWED_EXTENSIONS = new Set([".pdf", ".jpeg", ".jpg", ".png"])
  const QUALITY_CERT_ALLOWED_MIME_TYPES = new Set([
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ])

  const getTotalBytes = (files) =>
    (files || []).reduce((sum, f) => sum + (f?.size || 0), 0)

  const isQualityCertFileAllowed = (file) => {
    if (!file) return false
    const mimeOk = file.type
      ? QUALITY_CERT_ALLOWED_MIME_TYPES.has(file.type)
      : false
    const name = (file.name || "").toLowerCase()
    const ext = name.includes(".") ? `.${name.split(".").pop()}` : ""
    const extOk = ext ? QUALITY_CERT_ALLOWED_EXTENSIONS.has(ext) : false
    return mimeOk || extOk
  }

  const handleQualityCertificationsFileChange = (event) => {
    const newFiles = Array.from(event.target.files || [])
    setQualityCertificationsUploadError("")

    setQualityCertificationsFiles((prevFiles) => {
      const combinedFiles = [...prevFiles]
      const skippedInvalid = []

      newFiles.forEach((newFile) => {
        const isDuplicate = prevFiles.some(
          (prevFile) =>
            prevFile.name === newFile.name && prevFile.size === newFile.size,
        )
        if (isDuplicate) return

        if (!isQualityCertFileAllowed(newFile)) {
          skippedInvalid.push(newFile.name)
          return
        }

        combinedFiles.push(newFile)
      })

      const combinedBytes = getTotalBytes(combinedFiles)
      if (combinedBytes > MAX_QUALITY_CERTIFICATIONS_TOTAL_BYTES) {
        setQualityCertificationsUploadError(
          "Total upload size for Quality Certifications documents must be 10MB or less.",
        )
        setFieldValue("qualityCertificationsUpload", false, true)
        setFieldTouched("qualityCertificationsUpload", true, false)
        return prevFiles
      }

      if (skippedInvalid.length > 0) {
        setQualityCertificationsUploadError(
          `Skipped unsupported file(s): ${skippedInvalid.join(
            ", ",
          )}. Allowed: PDF, JPG/JPEG, PNG.`,
        )
      }

      const hasFiles = combinedFiles.length > 0
      if (hasFiles) {
        setFieldValue("qualityCertificationsUpload", true, true)
        setFieldTouched("qualityCertificationsUpload", true, false)
      } else {
        setFieldValue("qualityCertificationsUpload", false, true)
      }

      return combinedFiles
    })

    // Reset the input value to allow selecting the same file again if needed
    event.target.value = ""
  }

  const handleProgramsServicesFileChange = (event) => {
    const newFiles = Array.from(event.target.files || [])
    setProgramsServicesFiles((prevFiles) => {
      // Combine existing files with new files, avoiding duplicates
      const combinedFiles = [...prevFiles]
      newFiles.forEach((newFile) => {
        // Check if file already exists (by name and size)
        const isDuplicate = prevFiles.some(
          (prevFile) =>
            prevFile.name === newFile.name && prevFile.size === newFile.size,
        )
        if (!isDuplicate) {
          combinedFiles.push(newFile)
        }
      })
      return combinedFiles
    })
    setFieldValue("programsServicesUpload", true)
    // Reset the input value to allow selecting the same file again if needed
    event.target.value = ""
  }

  // Function to scroll to the first error field
  const scrollToFirstError = (errors) => {
    try {
      // Get the first error field name
      const firstErrorField = Object.keys(errors)[0]
      if (!firstErrorField) return

      // Wait a bit for the DOM to update with error messages
      setTimeout(() => {
        // Special handling for file upload fields
        if (firstErrorField === "goodsServicesUpload" || 
            firstErrorField === "certificationsUpload" || 
            firstErrorField === "programsServicesUpload" ||
            firstErrorField === "qualityCertificationsUpload") {
          // Find the file upload container with error class
          const fileUploadContainer = document.querySelector(
            `.${styles.fileUploadError}`
          )
          if (fileUploadContainer) {
            fileUploadContainer.scrollIntoView({
              behavior: "smooth",
              block: "center",
            })
            return
          }
        }

        // Try to find the input element by name or id
        let errorElement = document.querySelector(`input[name="${firstErrorField}"]`)
        if (!errorElement) {
          errorElement = document.querySelector(`textarea[name="${firstErrorField}"]`)
        }
        if (!errorElement) {
          errorElement = document.getElementById(firstErrorField)
        }
        // If still not found, try to find by the field's parent container with error class
        if (!errorElement) {
          const errorMessage = document.querySelector(
            `[name="${firstErrorField}"]`,
          )
          if (errorMessage) {
            errorElement = errorMessage.closest('.MuiFormControl-root') || 
                          errorMessage.closest('[class*="field"]') ||
                          errorMessage
          }
        }
        
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
          // Focus the input if it's an input or textarea
          if (errorElement.tagName === "INPUT" || errorElement.tagName === "TEXTAREA") {
            errorElement.focus()
          }
        }
      }, 100)
    } catch (error) {
      console.error("[DEBUG_LOG] Error scrolling to first error field:", error)
    }
  }

  const handleFormSubmit = async (e) => {
    try {
      console.log("[DEBUG_LOG] Form onSubmit handler called")
      e.preventDefault()

      // Log form details
      console.log("[DEBUG_LOG] Form ID:", e.target.id)
      console.log(
        "[DEBUG_LOG] Form valid state:",
        isSubmitting ? "submitting" : "not submitting",
      )

      // Use validateForm to get the most up-to-date validation state
      try {
        const validationErrors = await validateForm()
        console.log(
          "[DEBUG_LOG] Form has validation errors:",
          Object.keys(validationErrors).length > 0,
        )

        if (Object.keys(validationErrors).length > 0) {
          console.log("[DEBUG_LOG] Validation errors:", validationErrors)
          // Mark all fields as touched to show validation errors
          const allFields = Object.keys(values)
          const touchedFields = allFields.reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {},
          )
          setTouched(touchedFields)
          console.log(
            "[DEBUG_LOG] All fields marked as touched, errors should now be visible",
          )
          // Scroll to the first error field
          scrollToFirstError(validationErrors)
          console.log(
            "[DEBUG_LOG] Stopping form submission due to validation errors",
          )
          // Don't submit the form when there are validation errors
          return
        }

        // Only proceed if there are no validation errors
        if (onSubmit) {
          console.log(
            "[DEBUG_LOG] No validation errors, calling parent onSubmit with values",
          )
          const files = {}
          if (goodsServicesFiles.length > 0)
            files.goodsServicesFiles = goodsServicesFiles
          if (certificationsFiles.length > 0)
            files.certificationsFiles = certificationsFiles
          if (programsServicesFiles.length > 0)
            files.programsServicesFiles = programsServicesFiles
          if (qualityCertificationsFiles.length > 0)
            files.qualityCertificationsFiles = qualityCertificationsFiles
          onSubmit(values, files)
          console.log("[DEBUG_LOG] Parent onSubmit called successfully")
        } else {
          console.error("[DEBUG_LOG] No onSubmit prop provided to form")
        }
      } catch (validationError) {
        console.error(
          "[DEBUG_LOG] Error during form validation:",
          validationError,
        )
        return
      }
    } catch (error) {
      console.error("[DEBUG_LOG] Error in form submission:", error)
    }
  }

  return (
    <form className={styles.form} onSubmit={handleFormSubmit} id="formikForm">
      <Grid container spacing={2}>
        {/* Fields rendered using ConditionalField based on configuration */}
        <ConditionalField
          fieldName="organizationName"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="organizationName"
              className={styles.field}
              placeholder="Type your organization name"
              label="Organization Name"
              value={values.organizationName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.organizationName && touched.organizationName}
              errorMessage={
                errors.organizationName && touched.organizationName
                  ? errors.organizationName
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="organizationPhone"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="organizationPhone"
              className={styles.field}
              placeholder="Type your organization phone number"
              label="Organization Phone"
              value={values.organizationPhone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.organizationPhone && touched.organizationPhone}
              errorMessage={
                errors.organizationPhone && touched.organizationPhone
                  ? errors.organizationPhone
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="title"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Input
              name="title"
              className={styles.field}
              placeholder="Type your title"
              label="Title"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.title && touched.title}
              errorMessage={errors.title && touched.title ? errors.title : ""}
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="professionalLevel"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Dropdown
              id="professionalLevel"
              name="professionalLevel"
              label="What is your professional level?"
              value={
                values.professionalLevel
                  ? {
                      id: values.professionalLevel,
                      value:
                        professionalLevelItems.find(
                          (item) => item.id === values.professionalLevel,
                        )?.value || "",
                    }
                  : {}
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "professionalLevel", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={errors.professionalLevel && touched.professionalLevel}
              errorMessage={
                errors.professionalLevel && touched.professionalLevel
                  ? errors.professionalLevel
                  : ""
              }
              options={professionalLevelItems}
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="linkedinProfile"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Input
              name="linkedinProfile"
              className={styles.field}
              placeholder="https://www.linkedin.com/in/yourprofile"
              label="LinkedIn Profile"
              value={values.linkedinProfile}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.linkedinProfile && touched.linkedinProfile}
              errorMessage={
                errors.linkedinProfile && touched.linkedinProfile
                  ? errors.linkedinProfile
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="companyName"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="companyName"
              className={styles.field}
              placeholder="Type your company name"
              label="Company Name"
              value={values.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.companyName && touched.companyName}
              errorMessage={
                errors.companyName && touched.companyName
                  ? errors.companyName
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        {/* Company phone number field */}
        <ConditionalField
          fieldName="companyPhonenumber"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="companyPhonenumber"
              className={styles.field}
              placeholder="Type your company phone number"
              label="Company Phone Number"
              value={values.companyPhonenumber}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.companyPhonenumber && touched.companyPhonenumber}
              errorMessage={
                errors.companyPhonenumber && touched.companyPhonenumber
                  ? errors.companyPhonenumber
                  : ""
              }
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="streetAddress"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Input
              name="streetAddress"
              className={styles.field}
              placeholder="Type your Street Address"
              label="Street Address"
              value={values.streetAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.streetAddress && touched.streetAddress}
              errorMessage={
                errors.streetAddress && touched.streetAddress
                  ? errors.streetAddress
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="addressLine2"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Input
              name="addressLine2"
              className={styles.field}
              placeholder="Type your address line 2"
              label="Address Line 2"
              value={values.addressLine2}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.addressLine2 && touched.addressLine2}
              errorMessage={
                errors.addressLine2 && touched.addressLine2
                  ? errors.addressLine2
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="city"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="city"
              className={styles.field}
              placeholder="Type your city"
              label="City"
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.city && touched.city}
              errorMessage={errors.city && touched.city ? errors.city : ""}
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="stateRegion"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="stateRegion"
              className={styles.field}
              placeholder="Type your state / region / province"
              label="State / Region / Province"
              value={values.stateRegion}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.stateRegion && touched.stateRegion}
              errorMessage={
                errors.stateRegion && touched.stateRegion
                  ? errors.stateRegion
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="country"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="country"
              className={styles.field}
              placeholder="Type your country"
              label="Country"
              value={values.country}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.country && touched.country}
              errorMessage={
                errors.country && touched.country ? errors.country : ""
              }
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="zipCode"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="zipCode"
              className={styles.field}
              placeholder="Type your zip code"
              label="ZIP / Postal Code"
              value={values.zipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.zipCode && touched.zipCode}
              errorMessage={
                errors.zipCode && touched.zipCode ? errors.zipCode : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="website"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="website"
              className={styles.field}
              placeholder="example.com or https://www.example.com"
              label="Website"
              value={values.website}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.website && touched.website}
              errorMessage={
                errors.website && touched.website ? errors.website : ""
              }
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="primaryBusiness"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Describe your primary business:
            </label>
            <TextField
              name="primaryBusiness"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              placeholder="Describe your primary business"
              value={values.primaryBusiness}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.primaryBusiness && touched.primaryBusiness}
              helperText={
                errors.primaryBusiness && touched.primaryBusiness
                  ? errors.primaryBusiness
                  : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>
        {/* Headquarters country field */}
        <ConditionalField
          fieldName="headquartersCountry"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Dropdown
              name="headquartersCountry"
              className={styles.field}
              placeholder="Select headquarters country"
              label="Headquarters are located in which country?"
              value={
                values.headquartersCountry
                  ? {
                      id: values.headquartersCountry,
                      value:
                        countryItems.find(
                          (item) => item.id === values.headquartersCountry,
                        )?.value || "",
                    }
                  : {}
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "headquartersCountry", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={errors.headquartersCountry && touched.headquartersCountry}
              errorMessage={
                errors.headquartersCountry && touched.headquartersCountry
                  ? errors.headquartersCountry
                  : ""
              }
              options={countryItems}
              fullWidth
              id="headquartersCountry"
            />
          </Grid>
        </ConditionalField>

        {/* Area of Specialization field */}
        <ConditionalField
          fieldName="areaOfSpecialization"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="areaOfSpecialization"
              className={styles.field}
              placeholder="Type your area of specialization"
              label="Area of Specialization"
              value={values.areaOfSpecialization}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                errors.areaOfSpecialization && touched.areaOfSpecialization
              }
              errorMessage={
                errors.areaOfSpecialization && touched.areaOfSpecialization
                  ? errors.areaOfSpecialization
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        {/* Financial Services Type field */}
        <ConditionalField
          fieldName="financialServicesType"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Dropdown
              name="financialServicesType"
              className={styles.field}
              placeholder="Select Financial Services Type"
              label="Financial Services Type"
              value={
                values.financialServicesType
                  ? {
                      id: values.financialServicesType,
                      value:
                        financialServicesTypeItems.find(
                          (item) => item.id === values.financialServicesType,
                        )?.value || "",
                    }
                  : {}
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "financialServicesType", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={
                errors.financialServicesType && touched.financialServicesType
              }
              errorMessage={
                errors.financialServicesType && touched.financialServicesType
                  ? errors.financialServicesType
                  : ""
              }
              options={financialServicesTypeItems}
              fullWidth
              id="financialServicesType"
            />
          </Grid>
        </ConditionalField>

        {/* Industry & Sector field */}
        <ConditionalField
          fieldName="industrySector"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={6}>
            <Input
              name="industrySector"
              className={styles.field}
              placeholder="Type your industry & sector"
              label="Industry & Sector"
              value={values.industrySector}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.industrySector && touched.industrySector}
              errorMessage={
                errors.industrySector && touched.industrySector
                  ? errors.industrySector
                  : ""
              }
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="businessRegions"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Please select the regions you currently do business in:
              </FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionUS"
                    checked={values.regionUS}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="US"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionCanada"
                    checked={values.regionCanada}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Canada"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionEurope"
                    checked={values.regionEurope}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Europe"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionAsiaPacific"
                    checked={values.regionAsiaPacific}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Asia-Pacific"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionLatinAmerica"
                    checked={values.regionLatinAmerica}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Latin America"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionMiddleEast"
                    checked={values.regionMiddleEast}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Middle East"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="regionAfrica"
                    checked={values.regionAfrica}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    color="primary"
                  />
                }
                label="Africa"
              />
            </FormControl>
            {errors.businessRegions && touched.businessRegions && (
              <div className={styles.error}>{errors.businessRegions}</div>
            )}
          </Grid>
        </ConditionalField>
        {/* Financial services provider fields */}
        <ConditionalField
          fieldName="fundingOpportunities"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Funding Opportunities:
            </label>
            <TextField
              name="fundingOpportunities"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              placeholder="Describe funding or financing opportunities available"
              value={values.fundingOpportunities}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                errors.fundingOpportunities && touched.fundingOpportunities
              }
              helperText={
                errors.fundingOpportunities && touched.fundingOpportunities
                  ? errors.fundingOpportunities
                  : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="industrySpecialization"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Industry Specialization (if applicable):
            </label>
            <TextField
              name="industrySpecialization"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              placeholder="Describe your industry specialization"
              value={values.industrySpecialization}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                errors.industrySpecialization && touched.industrySpecialization
              }
              helperText={
                errors.industrySpecialization && touched.industrySpecialization
                  ? errors.industrySpecialization
                  : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="targetCompanyQualifications"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Target Company Qualifications:
            </label>
            <TextField
              name="targetCompanyQualifications"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              placeholder="Describe qualifications for target companies"
              value={values.targetCompanyQualifications}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                errors.targetCompanyQualifications &&
                touched.targetCompanyQualifications
              }
              helperText={
                errors.targetCompanyQualifications &&
                touched.targetCompanyQualifications
                  ? errors.targetCompanyQualifications
                  : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="programsServicesUpload"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Programs and Services Upload:
            </label>
            <div className={styles.fileUploadContainer}>
              <input
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
                id="programs-services-file"
                type="file"
                multiple
                onChange={handleProgramsServicesFileChange}
              />
              <label htmlFor="programs-services-file">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Document
                </Button>
              </label>
            </div>
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="revenueRangeAUM"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Dropdown
              name="revenueRangeAUM"
              className={styles.field}
              placeholder="Select Revenue or AUM Range"
              label="Revenue or Assets Under Management (AUM) Range"
              value={
                values.revenueRangeAUM
                  ? {
                      id: values.revenueRangeAUM,
                      value:
                        revenueRangeItems.find(
                          (item) => item.id === values.revenueRangeAUM,
                        )?.value || "",
                    }
                  : {}
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "revenueRangeAUM", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={errors.revenueRangeAUM && touched.revenueRangeAUM}
              errorMessage={
                errors.revenueRangeAUM && touched.revenueRangeAUM
                  ? errors.revenueRangeAUM
                  : ""
              }
              options={revenueRangeItems}
              fullWidth
              id="revenueRangeAUM"
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="revenueRange"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Dropdown
              name="revenueRange"
              className={styles.field}
              placeholder="Choose revenue level"
              label="Revenue Range"
              value={
                values.revenueRange
                  ? {
                      id: values.revenueRange,
                      value:
                        revenueRangeItems.find(
                          (item) => item.id === values.revenueRange,
                        )?.value || "",
                    }
                  : {}
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "revenueRange", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={errors.revenueRange && touched.revenueRange}
              errorMessage={
                errors.revenueRange && touched.revenueRange
                  ? errors.revenueRange
                  : ""
              }
              options={revenueRangeItems}
              fullWidth
              id="revenueRange"
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="customers"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              List your top 3 customers
            </label>
            <TextField
              name="customers"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              value={values.customers}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.customers && touched.customers}
              helperText={
                errors.customers && touched.customers ? errors.customers : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="goodsServicesUploadOption"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Description of Goods and Services Provided (Please include
              capabilities statement or marketing literature):
            </label>
            <FormControl component="fieldset" className={styles.formControl}>
              <RadioGroup
                name="goodsServicesUploadOption"
                value={values.goodsServicesUploadOption}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="text"
                  control={<Radio color="primary" />}
                  label="I would like to paste the text"
                />
                <FormControlLabel
                  value="upload"
                  control={<Radio color="primary" />}
                  label="I would like to upload a document"
                />
              </RadioGroup>
              {errors.goodsServicesUploadOption &&
                touched.goodsServicesUploadOption && (
                  <div className={styles.error}>
                    {errors.goodsServicesUploadOption}
                  </div>
                )}
            </FormControl>

            {values.goodsServicesUploadOption === "text" && (
              <TextField
                name="services"
                multiline
                rows={4}
                maxRows={24}
                className={styles.field}
                value={values.services}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.services && touched.services}
                helperText={
                  errors.services && touched.services ? errors.services : ""
                }
                fullWidth
              />
            )}

            {values.goodsServicesUploadOption === "upload" && (
              <div>
                <div
                  className={`${styles.fileUploadContainer} ${
                    errors.goodsServicesUpload && touched.goodsServicesUpload
                      ? styles.fileUploadError
                      : ""
                  }`}
                >
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    id="goods-services-file"
                    type="file"
                    multiple
                    onChange={handleGoodsServicesFileChange}
                  />
                  <label htmlFor="goods-services-file">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Document
                    </Button>
                  </label>
                  {goodsServicesFiles.length > 0 && (
                    <div className={styles.fileInfo}>
                      <span>
                        Selected files:{" "}
                        {goodsServicesFiles.map((f) => f.name).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
                {errors.goodsServicesUpload && touched.goodsServicesUpload && (
                  <div className={styles.error}>
                    {errors.goodsServicesUpload}
                  </div>
                )}
              </div>
            )}
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="qualityCertifications"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Please list quality Certifications (Example: ISO9001; TS16949,
              AS9100 Level D, etc.):
            </label>
            <FormControl component="fieldset" className={styles.formControl}>
              <RadioGroup
                name="qualityCertificationsUploadOption"
                value={values.qualityCertificationsUploadOption}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="text"
                  control={<Radio color="primary" />}
                  label="I would like to paste the text"
                />
                <FormControlLabel
                  value="upload"
                  control={<Radio color="primary" />}
                  label="I would like to upload document(s)"
                />
              </RadioGroup>
              {errors.qualityCertificationsUploadOption &&
                touched.qualityCertificationsUploadOption && (
                  <div className={styles.error}>
                    {errors.qualityCertificationsUploadOption}
                  </div>
                )}
            </FormControl>

            {values.qualityCertificationsUploadOption === "text" && (
              <TextField
                name="qualityCertifications"
                multiline
                rows={4}
                maxRows={24}
                className={styles.field}
                value={values.qualityCertifications}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  errors.qualityCertifications && touched.qualityCertifications
                }
                helperText={
                  errors.qualityCertifications && touched.qualityCertifications
                    ? errors.qualityCertifications
                    : ""
                }
                fullWidth
              />
            )}

            {values.qualityCertificationsUploadOption === "upload" && (
              <div>
                <div
                  className={`${styles.fileUploadContainer} ${
                    errors.qualityCertificationsUpload &&
                    touched.qualityCertificationsUpload
                      ? styles.fileUploadError
                      : ""
                  }`}
                >
                  <input
                    accept=".pdf,.jpeg,.jpg,.png"
                    style={{ display: "none" }}
                    id="quality-certifications-file"
                    type="file"
                    multiple
                    onChange={handleQualityCertificationsFileChange}
                  />
                  <label htmlFor="quality-certifications-file">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Document
                    </Button>
                  </label>
                  {qualityCertificationsFiles.length > 0 && (
                    <div className={styles.fileInfo}>
                      <span>
                        Selected files:{" "}
                        {qualityCertificationsFiles
                          .map((f) => f.name)
                          .join(", ")}
                      </span>
                      <div>
                        <small>
                          Total size:{" "}
                          {(getTotalBytes(qualityCertificationsFiles) /
                            (1024 * 1024)).toFixed(2)}
                          MB (max 10MB)
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                {qualityCertificationsUploadError ? (
                  <div className={styles.error}>
                    {qualityCertificationsUploadError}
                  </div>
                ) : (
                  errors.qualityCertificationsUpload &&
                  touched.qualityCertificationsUpload && (
                    <div className={styles.error}>
                      {errors.qualityCertificationsUpload}
                    </div>
                  )
                )}
              </div>
            )}
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="terms"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset" className={styles.formControl}>
              <FormLabel className={styles.labelFormLabel}>
                Are you a small and emerging business?
              </FormLabel>
              <RadioGroup
                name="terms"
                value={values.terms}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
              {errors.terms && touched.terms && (
                <div className={styles.error}>{errors.terms}</div>
              )}
            </FormControl>
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="isSolopreneur"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset" className={styles.formControl}>
              <FormLabel className={styles.labelFormLabel}>
                Are you a Solopreneur? (You are the sole owner and do not have
                any employees)
              </FormLabel>
              <RadioGroup
                name="isSolopreneur"
                value={values.isSolopreneur}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
              {errors.isSolopreneur && touched.isSolopreneur && (
                <div className={styles.error}>{errors.isSolopreneur}</div>
              )}
            </FormControl>
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="terms1"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset" className={styles.formControl}>
              <FormLabel className={styles.labelFormLabel}>
                Are you a diverse-owned and/or led company?
              </FormLabel>
              <RadioGroup
                name="terms1"
                value={values.terms1}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
              {errors.terms1 && touched.terms1 && (
                <div className={styles.error}>{errors.terms1}</div>
              )}
            </FormControl>
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="diversityDesignations"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
              Please select your demographic designation(s), if applicable:
              </FormLabel>
              <Grid container spacing={2}>
                {values.terms1 === "no" ? (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="preferenceNA"
                          checked={values.preferenceNA}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          color="primary"
                        />
                      }
                      label="N/A"
                    />
                  </Grid>
                ) : (
                  <>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference1"
                        checked={values.preference1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Asian-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference10"
                        checked={values.preference10}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Asian-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference2"
                        checked={values.preference2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Black-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference11"
                        checked={values.preference11}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Black-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference3"
                        checked={values.preference3}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Disabled-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference12"
                        checked={values.preference12}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Disabled-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference4"
                        checked={values.preference4}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Hispanic/Latin-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference13"
                        checked={values.preference13}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Hispanic/Latin-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference5"
                        checked={values.preference5}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="LGBTQ-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference14"
                        checked={values.preference14}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="LGBTQ-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference6"
                        checked={values.preference6}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Native American-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference15"
                        checked={values.preference15}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Native American-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference7"
                        checked={values.preference7}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Service-Disabled Veteran-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference16"
                        checked={values.preference16}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Service-Disabled Veteran-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference8"
                        checked={values.preference8}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Veteran-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference17"
                        checked={values.preference17}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Veteran-Led"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference9"
                        checked={values.preference9}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Women-Owned"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="preference18"
                        checked={values.preference18}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        color="primary"
                      />
                    }
                    label="Women-Led"
                  />
                </Grid>
                  </>
                )}
              </Grid>
              {errors.diversityDesignations &&
                touched.diversityDesignations && (
                  <div className={styles.error}>
                    {errors.diversityDesignations}
                  </div>
                )}
            </FormControl>
          </Grid>
        </ConditionalField>

        <ConditionalField
          fieldName="certificationsUploadOption"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Please list any relevant certifications (i.e., NMSDC, WBENC, &
              NGLCC, or Other):
            </label>
            <FormControl component="fieldset" className={styles.formControl}>
              <RadioGroup
                name="certificationsUploadOption"
                value={values.certificationsUploadOption}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="text"
                  control={<Radio color="primary" />}
                  label="I would like to paste the text"
                />
                <FormControlLabel
                  value="upload"
                  control={<Radio color="primary" />}
                  label="I would like to upload document(s)"
                />
              </RadioGroup>
              {errors.certificationsUploadOption &&
                touched.certificationsUploadOption && (
                  <div className={styles.error}>
                    {errors.certificationsUploadOption}
                  </div>
                )}
            </FormControl>

            {values.certificationsUploadOption === "text" && (
              <TextField
                name="certifications"
                multiline
                rows={4}
                maxRows={24}
                className={styles.field}
                value={values.certifications}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.certifications && touched.certifications}
                helperText={
                  errors.certifications && touched.certifications
                    ? errors.certifications
                    : ""
                }
                fullWidth
              />
            )}

            {values.certificationsUploadOption === "upload" && (
              <div>
                <div
                  className={`${styles.fileUploadContainer} ${
                    errors.certificationsUpload && touched.certificationsUpload
                      ? styles.fileUploadError
                      : ""
                  }`}
                >
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    id="certifications-file"
                    type="file"
                    multiple
                    onChange={handleCertificationsFileChange}
                  />
                  <label htmlFor="certifications-file">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Document
                    </Button>
                  </label>
                  {certificationsFiles.length > 0 && (
                    <div className={styles.fileInfo}>
                      <span>
                        Selected files:{" "}
                        {certificationsFiles.map((f) => f.name).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
                {errors.certificationsUpload && touched.certificationsUpload && (
                  <div className={styles.error}>
                    {errors.certificationsUpload}
                  </div>
                )}
              </div>
            )}
          </Grid>
        </ConditionalField>

        {/* US Services in More Than 3 States field */}
        <ConditionalField
          fieldName="providesServicesInMoreThanThreeStates"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset" className={styles.formControl}>
              <FormLabel className={styles.labelFormLabel}>
                If you are located in the United States, do you provide products
                or services in more than 3 states?
              </FormLabel>
              <RadioGroup
                name="providesServicesInMoreThanThreeStates"
                value={values.providesServicesInMoreThanThreeStates}
                onChange={handleChange}
                onBlur={handleBlur}
                row
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
              {errors.providesServicesInMoreThanThreeStates &&
                touched.providesServicesInMoreThanThreeStates && (
                  <div className={styles.error}>
                    {errors.providesServicesInMoreThanThreeStates}
                  </div>
                )}
            </FormControl>
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="additionalInfo"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <label className={styles.labelTextArea}>
              Provide additional information about your organization:
            </label>
            <TextField
              name="additionalInfo"
              multiline
              rows={4}
              maxRows={24}
              className={styles.field}
              value={values.additionalInfo}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.additionalInfo && touched.additionalInfo}
              helperText={
                errors.additionalInfo && touched.additionalInfo
                  ? errors.additionalInfo
                  : ""
              }
              fullWidth
            />
          </Grid>
        </ConditionalField>
        <ConditionalField
          fieldName="howDidYouLearn"
          businessType={businessType}
          category={category}
        >
          <Grid item xs={12} md={12}>
            <Dropdown
              id="howDidYouLearn"
              name="howDidYouLearn"
              className={styles.field}
              placeholder="Select how you learned about B2T"
              label="How did you learn about us?"
              fieldStyle="light"
              value={
                values.howDidYouLearn
                  ? {
                      id: values.howDidYouLearn,
                      value:
                        howDidYouLearnOptions.find(
                          (item) => item.id === values.howDidYouLearn,
                        )?.value || "",
                    }
                  : null
              }
              onChange={(name, option) =>
                handleChange({
                  target: { name: "howDidYouLearn", value: option.id },
                })
              }
              onBlur={handleBlur}
              error={errors.howDidYouLearn && touched.howDidYouLearn}
              errorMessage={
                errors.howDidYouLearn && touched.howDidYouLearn
                  ? errors.howDidYouLearn
                  : ""
              }
              options={howDidYouLearnOptions}
            />
          </Grid>
        </ConditionalField>
      </Grid>
    </form>
  )
}

const FormikApp = withFormik({
  mapPropsToValues(props) {
    return {
      // Standard fields
      companyName: props.initialValues?.companyName || "",
      companyPhonenumber: props.initialValues?.companyPhonenumber || "",
      streetAddress: props.initialValues?.streetAddress || "",
      addressLine2: props.initialValues?.addressLine2 || "",
      stateRegion: props.initialValues?.stateRegion || "",
      zipCode: props.initialValues?.zipCode || "",
      country: props.initialValues?.country || "",
      website: props.initialValues?.website || "",
      goodsServicesUploadOption:
        props.initialValues?.goodsServicesUploadOption || "text",
      services: props.initialValues?.services || "",
      goodsServicesUpload: props.initialValues?.goodsServicesUpload || false,
      certificationsUploadOption:
        props.initialValues?.certificationsUploadOption || "text",
      certifications: props.initialValues?.certifications || "",
      certificationsUpload: props.initialValues?.certificationsUpload || false,
      qualityCertifications: props.initialValues?.qualityCertifications || "",
      qualityCertificationsUploadOption:
        props.initialValues?.qualityCertificationsUploadOption || "text",
      qualityCertificationsUpload:
        props.initialValues?.qualityCertificationsUpload || false,
      terms: props.initialValues?.terms || null,
      terms1: props.initialValues?.terms1 || null,
      customers: props.initialValues?.customers || "",
      revenueRange: props.initialValues?.revenueRange || "",

      // Region fields
      regionUS: props.initialValues?.regionUS || false,
      regionCanada: props.initialValues?.regionCanada || false,
      regionEurope: props.initialValues?.regionEurope || false,
      regionAsiaPacific: props.initialValues?.regionAsiaPacific || false,
      regionLatinAmerica: props.initialValues?.regionLatinAmerica || false,
      regionMiddleEast: props.initialValues?.regionMiddleEast || false,
      regionAfrica: props.initialValues?.regionAfrica || false,
      // Computed field for businessRegions validation
      businessRegions:
        props.initialValues?.businessRegions ||
        !!(
          props.initialValues?.regionUS ||
          props.initialValues?.regionCanada ||
          props.initialValues?.regionEurope ||
          props.initialValues?.regionAsiaPacific ||
          props.initialValues?.regionLatinAmerica ||
          props.initialValues?.regionMiddleEast ||
          props.initialValues?.regionAfrica
        ),

      // Computed field for diversityDesignations validation
      diversityDesignations:
        props.initialValues?.diversityDesignations ||
        !!(
          props.initialValues?.preference1 ||
          props.initialValues?.preference2 ||
          props.initialValues?.preference3 ||
          props.initialValues?.preference4 ||
          props.initialValues?.preference5 ||
          props.initialValues?.preference6 ||
          props.initialValues?.preference7 ||
          props.initialValues?.preference8 ||
          props.initialValues?.preference9 ||
          props.initialValues?.preference10 ||
          props.initialValues?.preference11 ||
          props.initialValues?.preference12 ||
          props.initialValues?.preference13 ||
          props.initialValues?.preference14 ||
          props.initialValues?.preference15 ||
          props.initialValues?.preference16 ||
          props.initialValues?.preference17 ||
          props.initialValues?.preference18 ||
          props.initialValues?.preferenceNA
        ),

      // Diversity preferences
      preference1: props.initialValues?.preference1 || false,
      preference2: props.initialValues?.preference2 || false,
      preference3: props.initialValues?.preference3 || false,
      preference4: props.initialValues?.preference4 || false,
      preference5: props.initialValues?.preference5 || false,
      preference6: props.initialValues?.preference6 || false,
      preference7: props.initialValues?.preference7 || false,
      preference8: props.initialValues?.preference8 || false,
      preference9: props.initialValues?.preference9 || false,
      preference10: props.initialValues?.preference10 || false,
      preference11: props.initialValues?.preference11 || false,
      preference12: props.initialValues?.preference12 || false,
      preference13: props.initialValues?.preference13 || false,
      preference14: props.initialValues?.preference14 || false,
      preference15: props.initialValues?.preference15 || false,
      preference16: props.initialValues?.preference16 || false,
      preference17: props.initialValues?.preference17 || false,
      preference18: props.initialValues?.preference18 || false,
      preferenceNA: props.initialValues?.preferenceNA || false,

      // Additional info
      additionalInfo: props.initialValues?.additionalInfo || "",
      howDidYouLearn: props.initialValues?.howDidYouLearn || "",

      // New fields from CSV requirements
      // Non-profit organization fields
      organizationName: props.initialValues?.organizationName || "",
      organizationPhone: props.initialValues?.organizationPhone || "",
      title: props.initialValues?.title || "",
      areaOfSpecialization: props.initialValues?.areaOfSpecialization || "",
      programsServicesUpload:
        props.initialValues?.programsServicesUpload || false,

      // Financial services fields
      financialServicesType: props.initialValues?.financialServicesType || "",
      fundingOpportunities: props.initialValues?.fundingOpportunities || "",
      industrySpecialization: props.initialValues?.industrySpecialization || "",
      targetCompanyQualifications:
        props.initialValues?.targetCompanyQualifications || "",
      revenueRangeAUM: props.initialValues?.revenueRangeAUM || "",

      // Corporation provider fields
      headquartersCountry: props.initialValues?.headquartersCountry || "",
      providesServicesInMoreThanThreeStates:
        props.initialValues?.providesServicesInMoreThanThreeStates || "",

      // Emerging business fields
      isSolopreneur: props.initialValues?.isSolopreneur || "",
      industrySector: props.initialValues?.industrySector || "",

      // Other business fields
      city: props.initialValues?.city || "",
      primaryBusiness: props.initialValues?.primaryBusiness || "",

      // Professional fields
      linkedinProfile: props.initialValues?.linkedinProfile || "",
      professionalLevel: props.initialValues?.professionalLevel || "",
      region: props.initialValues?.region || "",

      // Get business type and category directly from step1Data
      whichBusinessType: props.step1Data?.whichBusinessType || "",
      bestDescribes: props.step1Data?.bestDescribes || "",
    }
  },
  validationSchema: Yup.object().shape({
    // Common fields for all business types (except professionals for some)
    streetAddress: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("streetAddress", type, category),
      then: Yup.string()
        .required("Please enter your street address")
        .min(5, "Please enter at least 5 characters for street address"),
      otherwise: Yup.string(),
    }),
    addressLine2: Yup.string(),
    stateRegion: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("stateRegion", type, category),
      then: Yup.string()
        .required("Please enter your state/region/province")
        .min(2, "Please enter at least 2 characters for state/region/province"),
      otherwise: Yup.string(),
    }),
    zipCode: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("zipCode", type, category),
      then: Yup.string()
        .required("Please enter your ZIP/Postal code")
        .matches(
          /^[0-9A-Z\s-]+$/,
          "Please enter a valid ZIP/Postal code format",
        ),
      otherwise: Yup.string(),
    }),
    country: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("country", type, category),
      then: Yup.string()
        .required("Please select your country")
        .min(2, "Please enter at least 2 characters for country"),
      otherwise: Yup.string(),
    }),
    website: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("website", type, category),
      then: Yup.string()
        .matches(
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i,
          "Please enter a valid website URL",
        )
        .required("Please enter your website URL"),
      otherwise: Yup.string(),
    }),
    terms: Yup.string()
      .nullable()
      .when(["whichBusinessType", "bestDescribes"], {
        is: (type, category) => isFieldVisible("terms", type, category),
        then: Yup.string()
          .required("Please indicate if you are a small and emerging business")
          .oneOf(
            ["yes", "no"],
            "Please indicate if you are a small and emerging business",
          ),
        otherwise: Yup.string().nullable(),
      }),
    terms1: Yup.string()
      .nullable()
      .when(["whichBusinessType", "bestDescribes"], {
        is: (type, category) => isFieldVisible("terms1", type, category),
        then: Yup.string()
          .required(
            "Please indicate if you are a small & emerging business or Supplier led company",
          )
          .oneOf(
            ["yes", "no"],
            "Please indicate if you are a small & emerging business or Supplier led company",
          ),
        otherwise: Yup.string().nullable(),
      }),
    additionalInfo: Yup.string(),
    howDidYouLearn: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("howDidYouLearn", type, category),
      then: Yup.string()
        .required("Please tell us how you learned about B2T"),
      otherwise: Yup.string(),
    }),

    // Standard company fields (conditionally required)
    companyName: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("companyName", type, category),
      then: Yup.string()
        .required("Please enter your company name")
        .min(2, "Please enter at least 2 characters for company name"),
      otherwise: Yup.string(),
    }),
    companyPhonenumber: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("companyPhonenumber", type, category),
        then: Yup.string()
          .required("Please enter your company phone number")
          .matches(
            /^[0-9+\s-()]+$/,
            "Please enter a valid phone number format",
          ),
        otherwise: Yup.string(),
      },
    ),
    industrySector: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("industrySector", type, category),
      then: Yup.string()
        .required("Please enter your industry & sector")
        .min(2, "Please enter at least 2 characters for industry & sector"),
      otherwise: Yup.string(),
    }),
    customers: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("customers", type, category),
      then: Yup.string().min(
        10,
        "Please provide more details about your customers",
      ),
      otherwise: Yup.string(),
    }),
    revenueRange: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("revenueRange", type, category),
      then: Yup.string()
        .required("Please select your revenue range")
        .oneOf(
          revenueRangeItems.map((item) => item.id),
          "Please select a valid revenue range",
        ),
      otherwise: Yup.string(),
    }),

    // Non-profit organization and Government agency fields (conditionally required)
    organizationName: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("organizationName", type, category),
        then: Yup.string()
          .required("Please enter your organization name")
          .min(2, "Please enter at least 2 characters for organization name"),
        otherwise: Yup.string(),
      },
    ),
    organizationPhone: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("organizationPhone", type, category),
        then: Yup.string()
          .required("Please enter your organization phone number")
          .matches(
            /^[0-9+\s-()]+$/,
            "Please enter a valid phone number format",
          ),
        otherwise: Yup.string(),
      },
    ),
    title: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("title", type, category),
      then: Yup.string()
        .required("Please enter your title")
        .min(2, "Please enter at least 2 characters for title"),
      otherwise: Yup.string(),
    }),
    areaOfSpecialization: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("areaOfSpecialization", type, category),
        then: Yup.string()
          .required("Please enter your area of specialization")
          .min(
            2,
            "Please enter at least 2 characters for area of specialization",
          ),
        otherwise: Yup.string(),
      },
    ),

    // Professional fields
    linkedinProfile: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("linkedinProfile", type, category),
      then: Yup.string()
        .url("Please enter a valid LinkedIn profile URL")
        .matches(
          /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/,
          "Please enter a valid LinkedIn profile URL",
        ),
      otherwise: Yup.string(),
    }),
    professionalLevel: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("professionalLevel", type, category),
        then: Yup.string()
          .required("Please select your professional level")
          .oneOf(
            professionalLevelItems.map((item) => item.id),
            "Please select a valid professional level",
          ),
        otherwise: Yup.string(),
      },
    ),

    // Financial services fields (conditionally required)
    financialServicesType: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("financialServicesType", type, category),
        then: Yup.string()
          .required("Please select your financial services type")
          .oneOf(
            financialServicesTypeItems.map((item) => item.id),
            "Please select a valid financial services type",
          ),
        otherwise: Yup.string(),
      },
    ),

    // Corporation, Financial, and Government provider fields (conditionally required)
    headquartersCountry: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("headquartersCountry", type, category),
        then: Yup.string()
          .required("Headquarters country is required")
          .oneOf(
            countryItems.map((item) => item.id),
            "Please select a valid country",
          ),
        otherwise: Yup.string(),
      },
    ),
    providesServicesInMoreThanThreeStates: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible(
            "providesServicesInMoreThanThreeStates",
            type,
            category,
          ),
        then: Yup.string()
          .required(
            "Please indicate if you provide services in more than 3 states",
          )
          .oneOf(["yes", "no"], "Please select Yes or No"),
        otherwise: Yup.string(),
      },
    ),
    fundingOpportunities: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("fundingOpportunities", type, category),
        then: Yup.string()
          .required("Please describe your funding opportunities")
          .min(
            10,
            "Please enter at least 10 characters for funding opportunities description",
          ),
        otherwise: Yup.string(),
      },
    ),
    targetCompanyQualifications: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("targetCompanyQualifications", type, category),
        then: Yup.string()
          .required("Please describe your target company qualifications")
          .min(
            10,
            "Please enter at least 10 characters for target company qualifications",
          ),
        otherwise: Yup.string(),
      },
    ),
    industrySpecialization: Yup.string(),
    revenueRangeAUM: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("revenueRangeAUM", type, category),
      then: Yup.string()
        .required("Please select your revenue or AUM range")
        .oneOf(
          revenueRangeItems.map((item) => item.id),
          "Please select a valid revenue or AUM range",
        ),
      otherwise: Yup.string(),
    }),

    // Emerging business fields (conditionally required)
    isSolopreneur: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("isSolopreneur", type, category),
      then: Yup.string()
        .required("Please indicate if you are a solopreneur")
        .oneOf(["yes", "no"], "Please select Yes or No"),
      otherwise: Yup.string(),
    }),

    // Other business fields (conditionally required)
    city: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("city", type, category),
      then: Yup.string()
        .required("Please enter your city")
        .min(2, "Please enter at least 2 characters for city"),
      otherwise: Yup.string(),
    }),

    // Business regions validation
    businessRegions: Yup.boolean().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("businessRegions", type, category),
        then: Yup.boolean()
          .required("Please select at least one business region")
          .oneOf([true], "Please select at least one business region"),
        otherwise: Yup.boolean(),
      },
    ),

    // Diversity designations validation
    diversityDesignations: Yup.boolean().when(
      ["whichBusinessType", "bestDescribes", "terms1"],
      {
        is: (type, category, terms1) =>
          isFieldVisible("diversityDesignations", type, category) &&
          terms1 === "yes",
        then: Yup.boolean()
          .required("Please select at least one diversity designation")
          .oneOf([true], "Please select at least one diversity designation"),
        otherwise: Yup.boolean(),
      },
    ),
    primaryBusiness: Yup.string().when(["whichBusinessType", "bestDescribes"], {
      is: (type, category) => isFieldVisible("primaryBusiness", type, category),
      then: Yup.string()
        .required("Please describe your primary business")
        .min(
          10,
          "Please enter at least 10 characters for primary business description",
        ),
      otherwise: Yup.string(),
    }),

    // Goods and services fields (conditionally required)
    goodsServicesUploadOption: Yup.string().when(
      ["whichBusinessType", "bestDescribes"],
      {
        is: (type, category) =>
          isFieldVisible("goodsServicesUploadOption", type, category),
        then: Yup.string()
          .required(
            "Please select how you want to provide goods and services information",
          )
          .oneOf(["text", "upload"], "Please select a valid option"),
        otherwise: Yup.string(),
      },
    ),
    services: Yup.string().when(
      ["goodsServicesUploadOption", "whichBusinessType", "bestDescribes"],
      {
        is: (option, type, category) =>
          option === "text" && isFieldVisible("services", type, category),
        then: Yup.string()
          .required("Please describe your goods and services")
          .min(
            10,
            "Please enter at least 10 characters for goods and services description",
          ),
        otherwise: Yup.string(),
      },
    ),
    goodsServicesUpload: Yup.boolean().when(
      ["goodsServicesUploadOption", "whichBusinessType", "bestDescribes"],
      {
        is: (option, type, category) =>
          option === "upload" &&
          isFieldVisible("goodsServicesUpload", type, category),
        then: Yup.boolean().oneOf([true], "Please upload at least one document"),
        otherwise: Yup.boolean(),
      },
    ),

    // Optional fields
    certificationsUploadOption: Yup.string().oneOf(
      ["text", "upload"],
      "Please select a valid option",
    ),
    certifications: Yup.string().when("certificationsUploadOption", {
      is: "text",
      then: Yup.string().min(
        10,
        "Certifications must be at least 10 characters",
      ),
      otherwise: Yup.string(),
    }),
    certificationsUpload: Yup.boolean().when("certificationsUploadOption", {
      is: "upload",
      then: Yup.boolean().oneOf([true], "Please upload at least one document"),
      otherwise: Yup.boolean(),
    }),
    qualityCertificationsUploadOption: Yup.string().oneOf(
      ["text", "upload"],
      "Please select a valid option",
    ),
    qualityCertificationsUpload: Yup.boolean().when(
      "qualityCertificationsUploadOption",
      {
        is: "upload",
        then: Yup.boolean().oneOf([true], "Please upload at least one document"),
        otherwise: Yup.boolean(),
      },
    ),
    qualityCertifications: Yup.string(),
  }),

  handleSubmit(values, { props, validateForm, setSubmitting, setTouched }) {
    console.log("[DEBUG_LOG] Formik handleSubmit called")
    validateForm()
      .then((errors) => {
        console.log(
          "[DEBUG_LOG] Form validation completed, errors:",
          Object.keys(errors).length > 0 ? Object.keys(errors) : "none",
        )
        if (Object.keys(errors).length === 0) {
          console.log(
            "[DEBUG_LOG] No validation errors, proceeding with submission",
          )
          if (props.onSubmit) {
            console.log("[DEBUG_LOG] Calling props.onSubmit with values")
            props.onSubmit(values)
          } else {
            console.error("[DEBUG_LOG] No props.onSubmit function provided")
          }
        } else {
          console.log(
            "[DEBUG_LOG] Validation errors found, marking fields as touched",
          )
          console.log("[DEBUG_LOG] Error fields:", Object.keys(errors))
          setSubmitting(false)
          // Mark all fields as touched to show all validation errors
          const allFields = Object.keys(values)
          const touchedFields = allFields.reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {},
          )
          console.log(
            "[DEBUG_LOG] Marking all fields as touched:",
            Object.keys(touchedFields).length,
          )
          setTouched(touchedFields)
          console.log(
            "[DEBUG_LOG] All fields marked as touched, errors should now be visible",
          )
          // Scroll to the first error field
          const firstErrorField = Object.keys(errors)[0]
          if (firstErrorField) {
            setTimeout(() => {
              let errorElement = document.querySelector(`input[name="${firstErrorField}"]`)
              if (!errorElement) {
                errorElement = document.querySelector(`textarea[name="${firstErrorField}"]`)
              }
              if (!errorElement) {
                errorElement = document.getElementById(firstErrorField)
              }
              if (!errorElement) {
                const fieldElement = document.querySelector(`[name="${firstErrorField}"]`)
                if (fieldElement) {
                  errorElement = fieldElement.closest('.MuiFormControl-root') || 
                                fieldElement.closest('[class*="field"]') ||
                                fieldElement
                }
              }
              if (errorElement) {
                errorElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                })
                if (errorElement.tagName === "INPUT" || errorElement.tagName === "TEXTAREA") {
                  errorElement.focus()
                }
              }
            }, 100)
          }
        }
      })
      .catch((error) => {
        console.error("[DEBUG_LOG] Error during form validation:", error)
      })
  },
})(SignUpApplyStep2Form)

SignUpApplyStep2Form.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  touched: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func,
  onValidityChange: PropTypes.func,
  dirty: PropTypes.bool,
  setFieldTouched: PropTypes.func,
  initialValues: PropTypes.object,
  setFieldValue: PropTypes.func,
  step1Data: PropTypes.object,
}

export default FormikApp
